import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap, take, tap, withLatestFrom } from 'rxjs/operators';
import { StorageService } from '@gelato-api-ui/sdk/src/lib/storage/storage.service';
import { PriceNavigatorStoreModel } from '@api-ui-app/src/app/price-navigator/prices/types/price-navigator.types';
import { EStoreType } from '@gelato-api-ui/core/e-commerce/e-store-type.enum';
import { CurrencyRateService } from '@gelato-api-ui/core/currency-rate/services/currency-rate.service';
import { toPriceNumber } from '@gelato-api-ui/core/product-prices/helpers/toPriceNumber';
import { AuthService } from '@gelato-api-ui/core/auth/auth.service';
import {
  DEFAULT_PLATFORM_COSTS,
  DEFAULT_ETSY_PLATFORM_COSTS,
  DEFAULT_SHOPIFY_PLATFORM_COSTS,
  DEFAULT_PLATFORM_COSTS_CURRENCY,
} from '../constants/default-platform-costs.constant';
import { PlatformCost, PlatformCostType } from '../types/platform-cost';

const LOCAL_STORAGE_PLATFORM_COSTS_KEY = 'pricing_table_platform_costs';

@Injectable({ providedIn: 'root' })
export class PlatformCostsService {
  selectedPlatformCosts$ = new BehaviorSubject<Record<string, PlatformCost>>({});
  private clientId$ = this.authService.getClientId();

  constructor(
    private readonly storage: StorageService,
    private readonly currencyRateService: CurrencyRateService,
    private readonly authService: AuthService,
  ) {}

  setPlatformCosts(
    platformCosts: Record<string, PlatformCost[]>,
    currency: string,
  ): Observable<Record<string, PlatformCost[]>> {
    this.selectedPlatformCosts$.next(this.getSelectedPlatformCosts(platformCosts));

    return this.convertPlatformCostsToDefaultCurrency(platformCosts, currency).pipe(
      withLatestFrom(this.clientId$),
      tap(([convertedPlatformCosts, clientId]) => {
        this.storage.setItem(
          LOCAL_STORAGE_PLATFORM_COSTS_KEY,
          JSON.stringify({ ...this.getStoredPlatformCosts(), [clientId]: convertedPlatformCosts }),
        );
      }),
      map(([platformCosts]) => platformCosts),
    );
  }

  getPlatformCostsByStores(
    stores: PriceNavigatorStoreModel[],
    currency: string,
  ): Observable<Record<string, PlatformCost[]>> {
    return this.getClientPlatformCosts().pipe(
      switchMap(platformCosts => {
        const defaultPlatformCosts = this.getDefaultPlatformCostsByStores(stores);

        return this.convertPlatformCostsToSelectedCurrency({ ...defaultPlatformCosts, ...platformCosts }, currency);
      }),
    );
  }

  convertSelectedPlatformCostsToSelectedCurrency(currency: string) {
    this.getClientPlatformCosts()
      .pipe(
        take(1),
        map(platformCosts => platformCosts && this.getSelectedPlatformCosts(platformCosts)),
        switchMap(selectedPlatformCosts =>
          selectedPlatformCosts ? this.convertPlatformCostsSelection(selectedPlatformCosts, currency) : of(null),
        ),
        tap(convertedPlatformCosts => this.selectedPlatformCosts$.next(convertedPlatformCosts)),
      )
      .subscribe();
  }

  private convertPlatformCostsSelection(
    selection: Record<string, PlatformCost>,
    currency,
  ): Observable<Record<string, PlatformCost>> {
    return this.currencyRateService.get(DEFAULT_PLATFORM_COSTS_CURRENCY, currency).pipe(
      take(1),
      map(rate =>
        Object.entries(selection).reduce((res, [storeId, selectedCost]) => {
          res[storeId] = this.convertCost(selectedCost, rate);
          return res;
        }, {}),
      ),
    );
  }

  private getSelectedPlatformCosts(platformCosts: Record<string, PlatformCost[]>): Record<string, PlatformCost> {
    return Object.entries(platformCosts).reduce((res, [storeId, items]) => {
      res[storeId] = items.find(({ selected }) => selected);

      return res;
    }, {});
  }

  private getClientPlatformCosts(): Observable<Record<string, PlatformCost[]>> {
    return this.clientId$.pipe(map(clientId => this.getStoredPlatformCosts()?.[clientId]));
  }

  private getStoredPlatformCosts(): Record<string, Record<string, PlatformCost[]>> {
    return JSON.parse(this.storage.getItem(LOCAL_STORAGE_PLATFORM_COSTS_KEY) ?? null);
  }

  private getDefaultPlatformCostsByStores(stores: PriceNavigatorStoreModel[]): Record<string, PlatformCost[]> {
    return stores.reduce((res, store) => {
      switch (store.type) {
        case EStoreType.SHOPIFY:
          res[store.storeId] = [...DEFAULT_SHOPIFY_PLATFORM_COSTS];
          break;
        case EStoreType.ETSY:
          res[store.storeId] = [...DEFAULT_ETSY_PLATFORM_COSTS];
          break;
        default:
          res[store.storeId] = [...DEFAULT_PLATFORM_COSTS];
          break;
      }

      return res;
    }, {});
  }

  private convertPlatformCostsToSelectedCurrency(
    platformCosts: Record<string, PlatformCost[]>,
    selectedCurrency: string,
  ): Observable<Record<string, PlatformCost[]>> {
    return this.convertPlatformCosts(platformCosts, DEFAULT_PLATFORM_COSTS_CURRENCY, selectedCurrency);
  }

  private convertPlatformCostsToDefaultCurrency(
    platformCosts: Record<string, PlatformCost[]>,
    selectedCurrency: string,
  ): Observable<Record<string, PlatformCost[]>> {
    return this.convertPlatformCosts(platformCosts, selectedCurrency, DEFAULT_PLATFORM_COSTS_CURRENCY);
  }

  private convertPlatformCosts(
    platformCosts: Record<string, PlatformCost[]>,
    sourceCurrency: string,
    destinationCurrency: string,
  ): Observable<Record<string, PlatformCost[]>> {
    return this.currencyRateService.get(sourceCurrency, destinationCurrency).pipe(
      map((rate: number) =>
        Object.entries(platformCosts).reduce((res, [storeId, costs]) => {
          res[storeId] = costs.map(cost => this.convertCost(cost, rate));
          return res;
        }, {}),
      ),
    );
  }

  private convertCost(cost: PlatformCost, rate: number): PlatformCost {
    if (cost.type === PlatformCostType.FixedAmount) {
      return {
        ...cost,
        value: toPriceNumber(cost.value * rate),
      };
    }

    if (cost.type === PlatformCostType.Mixed) {
      return {
        ...cost,
        extraAmount: toPriceNumber(cost.extraAmount * rate),
      };
    }

    return { ...cost };
  }
}
